'use client';

import { faClose, faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons';
import { faBarsFilter } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Drawer } from '@pickleballinc/react-ui';
import { Button } from '@pickleballinc/react-ui';
import clsx from 'clsx';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

import { useGetCountriesLookup, useGetStatesLookup } from '@/hooks/rankings/hooks';
import { useExportRankings } from '@/modules/apr/profile/hooks';
import { ExportRankingButton } from '@/modules/apr/rankings/ExportRankingsButton';
import LiveToggle from '@/modules/rankings/components/LiveToggle';
import { TournamentTypeButtons } from '@/modules/rankings/components/TournamentTypeButtons';
import { RankingsDropdownType } from '@/modules/rankings/constants';
import CountryMobile from '@/modules/rankings/MobileFilter/CountryMobile';
import StateMobile from '@/modules/rankings/MobileFilter/StateMobile';
import WeekMobile from '@/modules/rankings/MobileFilter/WeekMobile';
import { getNewSearchParams } from '@/modules/rankings/rankingsUtils';
import { PlayerFiltersInterface } from '@/types/ranking.types';
import { csvExportPlayersRankings } from '@/utils/actions/rankings/csvExportPlayersRankings';
import {
  getPartnerRankingsFileName,
  getWeeksInLastYear,
} from '@/utils/helpers/globalRankingsUtils';

interface MobileFilterProps {
  filters: PlayerFiltersInterface;
  setFilters: React.Dispatch<React.SetStateAction<PlayerFiltersInterface>>;
  canExportRankings: boolean;
}

export const MobileFilter = ({ filters, setFilters, canExportRankings }: MobileFilterProps) => {
  const router = useRouter();

  const { exportCsv, isExporting } = useExportRankings({
    exportFn: csvExportPlayersRankings,
    getFileName: getPartnerRankingsFileName,
  });

  // Export is hidden in LIVE mode and only available to users with export access.
  const showExport = canExportRankings && filters.live === '';

  const handleExport = () => {
    exportCsv({ params: getNewSearchParams(filters).toString() });
  };

  const { data: countriesData } = useGetCountriesLookup();
  const allCountriesOption = { id: '', title: 'All Countries' };
  const countriesList = [allCountriesOption, ...(countriesData?.results || [])];
  const country =
    countriesList.find((item) => String(item.id) === filters.country) ?? allCountriesOption;

  const { data: statesData } = useGetStatesLookup(String(country.id));
  const allStatesOption = { id: '', title: 'All States' };
  const statesList = [allStatesOption, ...(statesData?.results || [])];
  const state = statesList.find((item) => String(item.id) === filters.state) ?? allStatesOption;

  const weeksList = getWeeksInLastYear();
  const week = weeksList.find((item) => String(item.id) === filters.week) ?? weeksList[0];

  const handleReset = () => {
    router.push('?');
    setIsDrawerOpen(false);
  };

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<RankingsDropdownType>('');
  const [searchInput, setSearchInput] = useState('');
  const [stateSearchInput, setStateSearchInput] = useState('');
  const countryDropdownRef = useRef<HTMLDivElement>(null);
  const stateDropdownRef = useRef<HTMLDivElement>(null);
  const weekDropdownRef = useRef<HTMLDivElement>(null);

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  const toggleDropdown = (dropdown: RankingsDropdownType) => {
    setOpenDropdown(openDropdown === dropdown ? '' : dropdown);
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };

  const handleStateSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStateSearchInput(e.target.value);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      openDropdown === 'country' &&
      countryDropdownRef.current &&
      event.target instanceof HTMLElement &&
      !countryDropdownRef.current.contains(event.target)
    ) {
      setOpenDropdown('');
    }
    if (
      openDropdown === 'state' &&
      stateDropdownRef.current &&
      event.target instanceof HTMLElement &&
      !stateDropdownRef.current.contains(event.target)
    ) {
      setOpenDropdown('');
    }
    if (
      openDropdown === 'week' &&
      weekDropdownRef.current &&
      event.target instanceof HTMLElement &&
      !weekDropdownRef.current.contains(event.target)
    ) {
      setOpenDropdown('');
    }
  };

  const handleClickNext = () => {
    if (openDropdown === 'country') {
      setOpenDropdown('state');
    }
    if (openDropdown === 'state') {
      setOpenDropdown('week');
    }
    if (openDropdown === 'week') {
      setOpenDropdown('');
    }
  };

  const handleClickCancel = () => {
    if (openDropdown === 'country') {
      setOpenDropdown('');
    }
    if (openDropdown === 'state') {
      setOpenDropdown('');
    }
    if (openDropdown === 'week') {
      setOpenDropdown('');
    }
  };

  const handleApply = () => {
    router.replace(`?${getNewSearchParams(filters).toString()}`);
    setIsDrawerOpen(false);
  };

  useEffect(() => {
    if (isDrawerOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = 'auto';
      document.body.style.position = '';
      document.body.style.width = '';
    }
  }, [isDrawerOpen]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdown]);

  return (
    <div className='w-full'>
      <TournamentTypeButtons filters={filters} />
      <div className='flex h-[72px] w-full flex-row items-center'>
        <div className='mx-3 w-4/12'>
          <LiveToggle filters={filters} />
        </div>
        {showExport ? (
          <div className='flex w-4/12'>
            <ExportRankingButton
              onExport={handleExport}
              isExporting={isExporting}
              loaderBackgroundColor='bg-brand-700/90'
              className='!h-11 !w-full !justify-center'
            />
          </div>
        ) : null}
        <div
          className={clsx(
            'mx-3 flex h-11 flex-row items-center justify-center rounded-lg border border-gray-300 bg-white',
            showExport ? 'w-4/12' : 'w-8/12'
          )}
          onClick={toggleDrawer}
        >
          <span>
            <span className='p-3'>
              <FontAwesomeIcon icon={faBarsFilter} />
            </span>
          </span>
          <span>
            <div className='text-sm font-bold text-gray-900'>Filters</div>
          </span>
        </div>
      </div>
      <Drawer
        wrapperClassname='!h-dvh !w-full overflow-hidden'
        className={isDrawerOpen ? 'bg-black/70' : ''}
        open={isDrawerOpen}
        onBackdropClick={() => setIsDrawerOpen(false)}
      >
        <div className='relative flex h-full max-h-full max-w-full flex-col bg-gray-100'>
          <div className='flex items-center justify-between border-b border-gray-200 bg-white px-4 py-3'>
            <h2 className='text-lg font-semibold text-gray-900'>Filters</h2>
            <button
              onClick={toggleDrawer}
              aria-label='Close filters'
              className='flex size-9 items-center justify-center rounded-full border border-gray-300 bg-white text-lg text-gray-500'
            >
              <FontAwesomeIcon icon={faClose} />
            </button>
          </div>
          {/* The filters stack from the top and scroll as one column - the drawer used to
              space them apart, which left a large empty gap on a phone (PW-7597). */}
          <div className='flex flex-1 flex-col gap-3 overflow-y-auto p-4'>
            {filters.live === '' && (
              <CountryMobile
                country={country}
                countryDropdownOpen={openDropdown === 'country'}
                toggleCountryDropdown={() => toggleDropdown('country')}
                countryDropdownRef={countryDropdownRef}
                countriesList={countriesList}
                filters={filters}
                setFilters={setFilters}
                searchInput={searchInput}
                handleSearchInputChange={handleSearchInputChange}
                handleClickCancel={handleClickCancel}
                handleClickNext={handleClickNext}
              />
            )}

            {filters.live === '' && (
              <StateMobile
                state={state}
                stateDropdownOpen={openDropdown === 'state'}
                toggleStateDropdown={() => toggleDropdown('state')}
                stateDropdownRef={stateDropdownRef}
                stateSearchInput={stateSearchInput}
                statesList={statesList}
                filters={filters}
                setFilters={setFilters}
                handleStateSearchInputChange={handleStateSearchInputChange}
                handleClickCancel={handleClickCancel}
                handleClickNext={handleClickNext}
              />
            )}

            {filters.live === '' ? (
              <WeekMobile
                week={week}
                weekDropdownOpen={openDropdown === 'week'}
                toggleWeekDropdown={() => toggleDropdown('week')}
                weekDropdownRef={weekDropdownRef}
                weeksList={weeksList}
                filters={filters}
                setFilters={setFilters}
                handleClickCancel={handleClickCancel}
                handleClickNext={handleClickNext}
              />
            ) : null}

            {filters.live !== '' ? (
              <p className='text-sm text-gray-600'>
                Country, state and week filters aren&rsquo;t available while Live is on.
              </p>
            ) : null}
          </div>

          <div className='flex items-center justify-between gap-4 border-t border-gray-200 bg-white p-4'>
            <button
              className='text-purple-600'
              onClick={handleReset}
              data-testid='rankings-button-clear-all'
            >
              Clear all
            </button>
            <Button
              prefixIcon={<FontAwesomeIcon icon={faMagnifyingGlass} />}
              className='rounded-lg bg-experiment px-4 py-2 text-white'
              onClick={handleApply}
              variant='primary'
              data-testid='rankings-button-search'
            >
              Search
            </Button>
          </div>
        </div>
      </Drawer>
    </div>
  );
};
